import React, { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Upload, Eye, Edit, Trash2, FileText, Users, Target, Briefcase, Award, BarChart3, Mail, Phone, Settings, MoreVertical, Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import EdgeActions from '../components/ui/EdgeActions';
import FilterDrawer from '../components/ui/FilterDrawer';
import { toast } from 'react-hot-toast';

import { apiClient } from '../lib/api/client';
import { Candidate, CandidateCreate } from '../lib/types/api';
import { Button } from '../components/ui/Button';
import { useProcessingNotifications } from '../hooks/useProcessingNotifications';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';

 
import { CandidateForm } from '../components/candidates/CandidateForm';
import { CVUploadModal } from '../components/candidates/CVUploadModal';
 

export default function Candidates() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCandidateForm, setShowCandidateForm] = useState(false);
  const [showCVUpload, setShowCVUpload] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState<Candidate | null>(null);
  const [deletingCandidate, setDeletingCandidate] = useState<Candidate | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50); // Show 50 candidates per page
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [openMenuForId, setOpenMenuForId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [density] = useState<'comfortable' | 'compact'>('compact');
  
  const [quickFilter, setQuickFilter] = useState<'all' | 'submitted' | 'unsubmitted' | 'processing' | 'hasCV'>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState<string>('all');

  const queryClient = useQueryClient();
  
  // Enable processing notifications
  useProcessingNotifications();

  // Fetch requirements for the dropdown filter
  const { data: requirementsData } = useQuery({
    queryKey: ['requirements-list-for-filter'],
    queryFn: () => apiClient.getRequirementsList({
      page: 1,
      page_size: 100,
      status: 'active'
    }),
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });

  // Fetch candidates
  const { data: candidatesData, isLoading, error } = useQuery({
    queryKey: ['candidates', page, pageSize, searchQuery, selectedRequirement],
    queryFn: () => apiClient.getCandidates({
      page,
      page_size: pageSize,
      search: searchQuery || undefined
    }),
    placeholderData: (previousData) => previousData
  });

  const total = candidatesData?.total || 0;
  const totalPages = candidatesData?.total_pages || Math.ceil(total / pageSize);

  // Create candidate mutation
  const createCandidateMutation = useMutation({
    mutationFn: (data: CandidateCreate) => apiClient.createCandidate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      setShowCandidateForm(false);
      toast.success('Candidate created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create candidate');
    }
  });

  // Update candidate mutation
  const updateCandidateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Candidate> }) => 
      apiClient.updateCandidate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      setEditingCandidate(null);
      toast.success('Candidate updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update candidate');
    }
  });

  // Delete candidate mutation
  const deleteCandidateMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteCandidate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      setDeletingCandidate(null);
      toast.success('Candidate deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete candidate');
    }
  });

  const handleCreateCandidate = (data: CandidateCreate) => {
    createCandidateMutation.mutate(data);
  };

  const handleUpdateCandidate = (data: Partial<Candidate>) => {
    if (editingCandidate) {
      updateCandidateMutation.mutate({ id: editingCandidate.id, data });
    }
  };

  const handleDeleteCandidate = () => {
    if (deletingCandidate) {
      deleteCandidateMutation.mutate(deletingCandidate.id);
    }
  };

  const handleEditCandidate = (candidate: Candidate) => {
    setEditingCandidate(candidate);
    setShowCandidateForm(true);
  };

  const handleViewCandidate = (candidateId: string) => {
    navigate(`/candidates/${candidateId}`);
  };

  const handleViewSubmissions = (candidateId: string) => {
    navigate(`/candidates/${candidateId}/submissions`);
  };

  const handleDeleteClick = (candidate: Candidate) => {
    setDeletingCandidate(candidate);
  };

  const handleViewAnalysis = async (candidateId: string) => {
    try {
      const analyses = await apiClient.getCandidateAnalyses(candidateId);
      const completedAnalysis = analyses.analyses.find(a => a.status === 'completed');
      
      if (completedAnalysis) {
        navigate(`/candidates/${candidateId}/analysis/${completedAnalysis.requirement_id}`);
      } else {
        toast.error('No completed analysis found for this candidate');
      }
    } catch (error) {
      console.error('Failed to get candidate analyses:', error);
      toast.error('Failed to load analysis results');
    }
  };

  const handleCVUploadSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['candidates'] });
    setShowCVUpload(false);
    toast.success('CV uploaded successfully and is being processed');
  };

  // Poll for processing status updates
  React.useEffect(() => {
    const interval = setInterval(() => {
      // Only poll if we have candidates that might be processing
      const hasProcessingCandidates = candidatesData?.items?.some(candidate => 
        candidate.cv_path && 
        (!candidate.parsed_fields || 
         candidate.parsed_fields.status === 'processing' || 
         candidate.parsed_fields.status === 'pending')
      );
      
      if (hasProcessingCandidates) {
        queryClient.invalidateQueries({ queryKey: ['candidates'] });
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [candidatesData, queryClient]);

  const openActionsMenu = (event: React.MouseEvent, candidateId: string) => {
    event.stopPropagation();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const menuWidth = 192;
    const gap = 8;
    const top = rect.bottom + window.scrollY + gap;
    const left = rect.right + window.scrollX - menuWidth;
    setMenuPosition({ top, left: Math.max(8, left) });
    setOpenMenuForId(candidateId);
  };

  const closeActionsMenu = () => setOpenMenuForId(null);

  React.useEffect(() => {
    if (!openMenuForId) return;
    const onScrollOrResize = () => closeActionsMenu();
    const onDocClick = () => closeActionsMenu();
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') closeActionsMenu(); };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize, true);
    document.addEventListener('click', onDocClick);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize, true);
      document.removeEventListener('click', onDocClick);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenuForId]);

  const toggleRowExpansion = (candidateId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(candidateId)) {
        next.delete(candidateId);
      } else {
        next.add(candidateId);
      }
      return next;
    });
  };

  const formatDateTime = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso as string;
    }
  };

  // Derived list based on quick filters and requirement filter (in addition to server-side search)
  const filteredItems = React.useMemo(() => {
    const items = candidatesData?.items || [];
    return items.filter((candidate: any) => {
      // Apply quick filter
      let quickFilterMatch = true;
      switch (quickFilter) {
        case 'submitted':
          quickFilterMatch = (candidate.submissions?.length || candidate.submitted_requirements?.length || 0) > 0;
          break;
        case 'unsubmitted':
          quickFilterMatch = (candidate.submissions?.length || candidate.submitted_requirements?.length || 0) === 0;
          break;
        case 'processing':
          quickFilterMatch = !!candidate.cv_path && (!candidate.parsed_fields || ['processing', 'pending'].includes(candidate.parsed_fields.status));
          break;
        case 'hasCV':
          quickFilterMatch = !!candidate.cv_path;
          break;
        default:
          quickFilterMatch = true;
      }
      
      // Apply requirement filter
      let requirementMatch = true;
      if (selectedRequirement && selectedRequirement !== 'all') {
        const submissions = candidate.submissions || candidate.submitted_requirements || [];
        requirementMatch = submissions.some((sub: any) => sub.requirement_id === selectedRequirement);
      }
      
      return quickFilterMatch && requirementMatch;
    });
  }, [candidatesData, quickFilter, selectedRequirement]);

  const displayItems = filteredItems;
  const headY = density === 'compact' ? 'py-3' : 'py-5';
  const cellY = density === 'compact' ? 'py-3' : 'py-5';
  const headYSm = density === 'compact' ? 'py-2' : 'py-3';
  const cellYSm = density === 'compact' ? 'py-2' : 'py-4';



  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-error-500 dark:text-error-500 mb-4">Failed to load candidates</div>
        <Button onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="space-y-6">
        <style dangerouslySetInnerHTML={{ __html: `@keyframes genieIn { 0% { opacity: 0; transform: translateY(-8px) scaleY(0.8); filter: blur(2px);} 60% { opacity: 1; transform: translateY(0) scaleY(1.02); filter: blur(0.5px);} 100% { opacity: 1; transform: translateY(0) scaleY(1); filter: blur(0);} }` }} />
        {openMenuForId && (
          <div className="fixed z-[150]" style={{ top: menuPosition.top, left: menuPosition.left }}>
            <div className="w-48 bg-glass-surface/95 backdrop-blur border border-glass-border/50 rounded-lg shadow-2xl ring-1 ring-black/5"
              style={{ animation: 'genieIn 180ms cubic-bezier(0.16, 1, 0.3, 1)', transformOrigin: 'top right' }}
            >
              <button onClick={() => { if (openMenuForId) handleViewCandidate(openMenuForId); closeActionsMenu(); }} className="w-full text-left px-3 py-2 hover:bg-glass-surface/60 flex items-center gap-2">
                <Eye className="w-4 h-4" /> View
              </button>
              <button onClick={() => { if (openMenuForId) handleViewAnalysis(openMenuForId); closeActionsMenu(); }} className="w-full text-left px-3 py-2 hover:bg-glass-surface/60 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Analysis
              </button>
              <button onClick={() => { const c = candidatesData?.items?.find((x:any)=>x.id===openMenuForId); if (c) handleEditCandidate(c); closeActionsMenu(); }} className="w-full text-left px-3 py-2 hover:bg-glass-surface/60 flex items-center gap-2">
                <Edit className="w-4 h-4" /> Edit
              </button>
              <button onClick={() => { const c = candidatesData?.items?.find((x:any)=>x.id===openMenuForId); if (c) handleDeleteClick(c); closeActionsMenu(); }} className="w-full text-left px-3 py-2 hover:bg-red-500/10 text-red-500 flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        )}
        {/* Toolbar removed to free space */}

        {/* Vertical right-edge Upload and Add icons above FILTERS */}
        <EdgeActions
          onUpload={() => setShowCVUpload(true)}
          onAdd={() => setShowCandidateForm(true)}
          onFilter={() => setDrawerOpen(true)}
        />

        {/* End Toolbar */}

        {/* Search and Filter Controls Above Table */}
        <div className="mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Search Box */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-enterprise-tertiary" />
              <Input
                type="text"
                placeholder="Search candidates by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-10 py-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 backdrop-blur-xl border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg text-text-enterprise-primary placeholder-text-enterprise-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setPage(1);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-enterprise-tertiary hover:text-text-enterprise-primary transition-colors"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Requirement Filter Dropdown */}
            <div className="relative z-50">
              <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-enterprise-tertiary pointer-events-none z-10" />
              <select
                value={selectedRequirement}
                onChange={(e) => {
                  setSelectedRequirement(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-10 py-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 backdrop-blur-xl border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg text-text-enterprise-primary focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-200 cursor-pointer relative z-50"
                title="Filter by requirement/portal"
                style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist', appearance: 'auto' }}
              >
                <option value="all">All Requirements/Portals</option>
                {requirementsData?.requirements?.map((req: any) => (
                  <option key={req.id} value={req.id}>
                    {req.title || 'Untitled Requirement'}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
                <svg className="w-4 h-4 text-text-enterprise-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          
          {/* Search/Filter Results */}
          {(searchQuery || selectedRequirement !== 'all') && (
            <div className="text-xs text-text-enterprise-secondary flex items-center gap-2">
              {isLoading ? (
                <span className="flex items-center gap-1">
                  <LoadingSpinner size="xs" variant="ai" />
                  Filtering...
                </span>
              ) : (
                <>
                  <span>Showing {filteredItems.length} of {total} candidate{total !== 1 ? 's' : ''}</span>
                  {(searchQuery || selectedRequirement !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedRequirement('all');
                        setPage(1);
                      }}
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Clear filters
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Enhanced Candidates Table */}
        <div className="relative overflow-hidden rounded-2xl border border-glass-border/50 dark:border-glass-border-dark-mode/50 shadow-glass-lg bg-gradient-to-br from-glass-surface/80 via-glass-surface/90 to-glass-surface/80 dark:from-glass-surface-dark-mode/80 dark:via-glass-surface-dark-mode/90 dark:to-glass-surface-dark-mode/80 backdrop-blur-xl">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-primary-500/20 to-secondary-500/15 rounded-full blur-2xl opacity-30 animate-pulse"></div>
            <div className="absolute -bottom-20 -left-20 w-32 h-32 bg-gradient-to-br from-secondary-500/20 to-accent-500/15 rounded-full blur-2xl opacity-25 animate-pulse delay-1000"></div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16 relative z-10">
              <LoadingSpinner size="xl" variant="ai" />
            </div>
          ) : displayItems.length === 0 ? (
            <div className="text-center py-16 px-8 relative z-10">
              <div className="mb-8">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-full flex items-center justify-center border border-primary-500/30">
                  <Users className="w-10 h-10 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-bold text-text-enterprise-primary mb-2">
                  No candidates found
                </h3>
                <p className="text-text-enterprise-secondary text-base">
                  Get started by adding your first candidate or uploading a CV
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="enterprise"
                  onClick={() => setShowCandidateForm(true)}
                  className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Candidate
                </Button>
                <Button 
                  variant="glass" 
                  onClick={() => setShowCVUpload(true)}
                  className="w-full sm:w-auto px-6 py-3 border border-glass-border/50 hover:border-primary-500/50 hover:text-primary-400 transition-all duration-200"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  Upload CV
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto relative z-10 -mx-2 sm:mx-0" style={{ maxWidth: '100%' }}>
              {/* Mobile Card View */}
              <div className="block lg:hidden p-4 space-y-4">
                {displayItems.map((candidate: any, index: number) => {
                  const submissionCount = candidate.submissions?.length || candidate.submitted_requirements?.length || 0;
                  const isExpanded = expandedRows.has(candidate.id);
                  return (
                    <div key={candidate.id} className="bg-gradient-to-br from-glass-surface/95 to-glass-surface/90 backdrop-blur-xl rounded-2xl shadow-lg border border-glass-border/50 p-4 hover:shadow-xl transition-all duration-500 hover:scale-105 hover:-translate-y-1 animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                            {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                          </div>
                          <div className="min-w-0">
                            <button 
                              onClick={() => handleViewCandidate(candidate.id)}
                              className="font-bold text-lg text-text-enterprise-primary truncate text-left hover:underline cursor-pointer"
                            >
                              {candidate.name || 'Unknown Candidate'}
                            </button>
                            <p className="text-sm text-text-enterprise-secondary truncate">
                              {candidate.email || 'No email'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleViewCandidate(candidate.id)}
                            size="sm"
                            className="p-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleViewAnalysis(candidate.id)}
                            variant="outline"
                            size="sm"
                            className="p-2 border-primary-500/50 hover:bg-primary-500/10 text-primary-600 dark:text-primary-400 rounded-lg"
                            title="View analysis"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCandidate(candidate);
                            }}
                            variant="outline"
                            size="sm"
                            className="p-2 border-blue-500/50 hover:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-lg"
                            title="Edit candidate"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center gap-2 p-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
                          <Briefcase className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                          <span className="text-sm font-medium text-text-enterprise-primary">
                            {candidate.parsed_fields?.experience_years ? `${candidate.parsed_fields.experience_years}y` : 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
                          <Award className="w-4 h-4 text-green-600 dark:text-green-500" />
                          {submissionCount > 0 ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewSubmissions(candidate.id);
                              }}
                              className="text-sm font-medium text-text-enterprise-primary hover:text-green-400 transition-colors cursor-pointer"
                            >
                              {submissionCount} submitted
                            </button>
                          ) : (
                            <span className="text-sm font-medium text-text-enterprise-primary">
                              No submissions
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Expandable Details */}
                      <div className="border-t border-glass-border/30 pt-3">
                        <button
                          onClick={() => toggleRowExpansion(candidate.id)}
                          className="w-full flex items-center justify-between text-sm text-text-enterprise-secondary hover:text-primary-400 transition-colors"
                        >
                          <span>{isExpanded ? 'Show less' : 'Show more details'}</span>
                          <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                        </button>
                        
                        {isExpanded && (
                          <div className="mt-4 space-y-4 animate-fade-in">
                            {/* Contact Information */}
                            <div>
                              <h4 className="text-sm font-semibold text-text-enterprise-primary mb-2 flex items-center gap-2">
                                <Mail className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                Contact
                              </h4>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 p-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
                                  <Mail className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                                  <span className="text-sm font-medium text-text-enterprise-primary truncate">
                                    {candidate.email || 'Not provided'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 p-2 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
                                  <Phone className="w-4 h-4 text-green-600 dark:text-green-500" />
                                  <span className="text-sm font-medium text-text-enterprise-primary truncate">
                                    {candidate.phone || 'Not provided'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Skills */}
                            {(candidate.parsed_fields?.skills || []).length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-text-enterprise-primary mb-2 flex items-center gap-2">
                                  <Target className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                  Skills
                                </h4>
                                <div className="flex flex-wrap gap-1">
                                  {(candidate.parsed_fields?.skills || []).slice(0, 6).map((skill: string, index: number) => (
                                    <span
                                      key={index}
                                      className="px-2 py-1 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 text-primary-700 dark:text-primary-300 text-xs rounded border border-primary-500/30 font-medium"
                                    >
                                      {skill.length > 12 ? skill.substring(0, 12) + '...' : skill}
                                    </span>
                                  ))}
                                  {(candidate.parsed_fields?.skills || []).length > 6 && (
                                    <span className="px-2 py-1 bg-glass-surface/50 text-text-enterprise-tertiary text-xs rounded border border-glass-border/30 font-medium">
                                      +{(candidate.parsed_fields?.skills || []).length - 6}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Additional Info */}
                            <div>
                              <h4 className="text-sm font-semibold text-text-enterprise-primary mb-2 flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                Additional Info
                              </h4>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between p-2 bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-lg border border-orange-500/20">
                                  <span className="text-sm font-medium text-text-enterprise-primary">Position:</span>
                                  <span className="text-sm text-text-enterprise-secondary">
                                    {candidate.parsed_fields?.current_position || 'Not specified'}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between p-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20">
                                  <span className="text-sm font-medium text-text-enterprise-primary">CV Status:</span>
                                  <span className="text-sm">
                                    {candidate.cv_path ? (
                                      <span className="inline-flex items-center px-2 py-1 bg-green-500/20 text-green-700 dark:text-green-400 text-xs rounded border border-green-500/30 font-medium">
                                        Available
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-1 bg-background-300/20 dark:bg-background-dark-300/20 text-text-tertiary dark:text-text-tertiary text-xs rounded border border-border-200 dark:border-border-dark-200 font-medium">
                                        Not uploaded
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tablet Table View */}
              <div className="hidden md:block lg:hidden">
                <div className="rounded-xl border border-glass-border/40 dark:border-glass-border-dark-mode/40 shadow-sm bg-gradient-to-r from-glass-surface/80 via-glass-surface/90 to-glass-surface/80 dark:from-glass-surface-dark-mode/80 dark:via-glass-surface-dark-mode/90 dark:to-glass-surface-dark-mode/80 backdrop-blur-xl overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full table-auto" style={{ tableLayout: 'auto' }}>
                  <colgroup>
                    <col style={{ width: '40%', minWidth: '200px' }} />
                    <col style={{ width: '20%', minWidth: '100px' }} />
                    <col style={{ width: '25%', minWidth: '120px' }} />
                    <col style={{ width: '15%', minWidth: '80px' }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-gradient-to-r from-glass-primary/40 via-glass-secondary/35 to-glass-primary/40 border-b border-glass-border/50 backdrop-blur-xl">
                    <tr>
                      <th className={`px-4 ${headYSm} text-left ds-table-header`}>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-sm font-bold text-text-enterprise-primary uppercase tracking-wider">Candidate</span>
                        </div>
                      </th>
                      <th className={`px-4 ${headYSm} text-left ds-table-header`}>
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-sm font-bold text-text-enterprise-primary uppercase tracking-wider">Experience</span>
                        </div>
                      </th>
                      <th className={`px-4 ${headYSm} text-left ds-table-header`}>
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-sm font-bold text-text-enterprise-primary uppercase tracking-wider">Submissions</span>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left ds-table-header">
                        <div className="flex items-center gap-2">
                          <Settings className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-sm font-bold text-text-enterprise-primary uppercase tracking-wider">Actions</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-glass-border/30">
                    {displayItems.map((candidate: any) => {
                      const isExpanded = expandedRows.has(candidate.id);
                      const submissionCount = candidate.submissions?.length || candidate.submitted_requirements?.length || 0;
                      return (
                      <React.Fragment key={candidate.id}>
                      <tr className="hover:bg-gradient-to-r hover:from-glass-surface/20 hover:via-glass-surface/30 hover:to-glass-surface/20 transition-all duration-300 cursor-pointer hover:scale-[1.01] hover:-translate-y-0.5" onClick={() => toggleRowExpansion(candidate.id)}>
                        <td className={`px-4 ${cellYSm} ds-table-cell`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                              {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                            </div>
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewCandidate(candidate.id);
                                }}
                                className="font-semibold text-text-enterprise-primary text-sm truncate block w-full text-left hover:underline cursor-pointer"
                                title={candidate.name || 'Unknown'}
                              >
                                {candidate.name || 'Unknown'}
                              </button>
                              <div className="text-xs text-text-enterprise-secondary truncate" title={candidate.email || 'No email'}>{candidate.email || 'No email'}</div>
                              <div className="text-xs text-text-enterprise-tertiary truncate" title={candidate.phone || 'No phone'}>{candidate.phone || 'No phone'}</div>
                            </div>
                          </div>
                        </td>
                        <td className={`px-4 ${cellYSm} ds-table-cell`}>
                          <div className="flex items-center gap-1">
                            <Briefcase className="w-3 h-3 text-primary-600 dark:text-primary-400" />
                            <span className="text-sm font-semibold text-text-enterprise-primary">
                              {candidate.parsed_fields?.experience_years ? `${candidate.parsed_fields.experience_years}y` : 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className={`px-4 ${cellYSm} ds-table-cell`}>
                          <div className="flex items-center gap-1">
                            <Award className="w-3 h-3 text-primary-600 dark:text-primary-400" />
                            {submissionCount > 0 ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewSubmissions(candidate.id);
                                }}
                                className="text-sm font-semibold text-text-enterprise-primary hover:text-primary-300 transition-colors cursor-pointer"
                              >
                                {submissionCount}
                              </button>
                            ) : (
                              <span className="text-text-enterprise-tertiary text-xs font-medium">None</span>
                            )}
                          </div>
                        </td>
                        <td className={`px-4 ${cellYSm} ds-table-cell`}>
                          <div className="flex justify-end">
                            <button
                              className="p-2 rounded-md bg-glass-surface/30 hover:bg-glass-surface/50 border border-glass-border/40 transition-colors"
                              onClick={(e) => openActionsMenu(e, candidate.id)}
                              aria-label="Open actions"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gradient-to-r from-glass-surface/20 to-glass-surface/10">
                          <td colSpan={4} className="px-4 py-4">
                            <div className="space-y-4">
                              <div>
                                <h4 className="text-sm font-semibold text-text-enterprise-primary mb-2 flex items-center gap-2">
                                  <Target className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                  Skills
                                </h4>
                                {(candidate.parsed_fields?.skills || []).length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {(candidate.parsed_fields?.skills || []).slice(0, 6).map((skill: string, index: number) => (
                                      <span key={index} className="px-2 py-1 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 text-primary-300 text-xs rounded border border-primary-500/30 font-medium">
                                        {skill}
                                      </span>
                                    ))}
                                    {(candidate.parsed_fields?.skills || []).length > 6 && (
                                      <span className="px-2 py-1 bg-glass-surface/50 text-text-enterprise-tertiary text-xs rounded border border-glass-border/30 font-medium">
                                        +{(candidate.parsed_fields?.skills || []).length - 6} more
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-sm text-text-enterprise-tertiary">No skills parsed</div>
                                )}
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-text-enterprise-primary mb-2 flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                  Last Updated
                                </h4>
                                <div className="text-sm text-text-enterprise-secondary">{formatDateTime(candidate.updated_at)}</div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );})}
                  </tbody>
                </table>
                </div>
                </div>
              </div>

              {/* Enhanced Desktop Table View */}
              <div className="hidden lg:block">
              <div className="rounded-xl border border-glass-border/40 dark:border-glass-border-dark-mode/40 shadow-sm bg-gradient-to-r from-glass-surface/80 via-glass-surface/90 to-glass-surface/80 dark:from-glass-surface-dark-mode/80 dark:via-glass-surface-dark-mode/90 dark:to-glass-surface-dark-mode/80 backdrop-blur-xl overflow-hidden w-full">
              <div className="w-full overflow-hidden">
              <table className="w-full" style={{ tableLayout: 'auto' }}>
                  <colgroup>
                    <col style={{ width: '25%', minWidth: '180px' }} />
                    <col style={{ width: '12%', minWidth: '90px' }} />
                    <col style={{ width: '15%', minWidth: '100px' }} />
                    <col style={{ width: '20%', minWidth: '140px' }} />
                    <col style={{ width: '18%', minWidth: '130px' }} />
                    <col style={{ width: '10%', minWidth: '70px' }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-gradient-to-r from-glass-primary/40 via-glass-secondary/35 to-glass-primary/40 border-b border-glass-border/50 backdrop-blur-xl">
                    <tr>
                      <th className={`px-4 ${headY} text-left ds-table-header`}>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-xs font-bold text-text-enterprise-primary uppercase tracking-wider">Candidate</span>
                        </div>
                    </th>
                      <th className={`px-3 ${headY} text-left ds-table-header`}>
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-xs font-bold text-text-enterprise-primary uppercase tracking-wider">Exp.</span>
                        </div>
                    </th>
                      <th className={`px-3 ${headY} text-left ds-table-header`}>
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-xs font-bold text-text-enterprise-primary uppercase tracking-wider">Sub.</span>
                        </div>
                    </th>
                      <th className={`px-3 ${headY} text-left ds-table-header`}>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-xs font-bold text-text-enterprise-primary uppercase tracking-wider">By</span>
                        </div>
                    </th>
                      <th className={`px-3 ${headY} text-left ds-table-header`}>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-xs font-bold text-text-enterprise-primary uppercase tracking-wider">Updated</span>
                        </div>
                    </th>
                      <th className={`px-3 ${headY} text-right ds-table-header`}>
                        <div className="flex items-center gap-2 justify-end">
                          <Settings className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-xs font-bold text-text-enterprise-primary uppercase tracking-wider">Actions</span>
                        </div>
                    </th>
                  </tr>
                </thead>
                  <tbody className="divide-y divide-glass-border/30">
                  {candidatesData?.items?.map((candidate: Candidate) => {
                    const isExpanded = expandedRows.has(candidate.id);
                    const submissionCount = candidate.submissions?.length || candidate.submitted_requirements?.length || 0;
                    return (
                    <React.Fragment key={candidate.id}>
                      <tr className="group hover:bg-gradient-to-r hover:from-glass-surface/20 hover:via-glass-surface/30 hover:to-glass-surface/20 transition-all duration-300 cursor-pointer hover:scale-[1.01] hover:-translate-y-0.5" onClick={() => toggleRowExpansion(candidate.id)}>
                        <td className={`px-4 ${cellY} ds-table-cell`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500/20 via-primary-600/30 to-secondary-500/20 rounded-full flex items-center justify-center border border-primary-500/30 group-hover:border-primary-400/50 transition-all duration-200 flex-shrink-0">
                              <span className="text-primary-600 dark:text-primary-400 font-bold text-base">
                              {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                            </span>
                          </div>
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewCandidate(candidate.id);
                                }}
                                className="font-bold text-text-enterprise-primary text-base group-hover:text-primary-300 transition-colors truncate block w-full text-left hover:underline cursor-pointer"
                                title={candidate.name || 'Unknown'}
                              >
                                {candidate.name || 'Unknown'}
                              </button>
                              <div className="text-xs text-text-enterprise-secondary truncate" title={candidate.email || 'No email'}>
                                {candidate.email || 'No email'}
                              </div>
                              <div className="text-xs text-text-enterprise-tertiary truncate" title={candidate.phone || 'No phone'}>
                                {candidate.phone || 'No phone'}
                              </div>
                          </div>
                        </div>
                      </td>
                        <td className={`px-3 ${cellY} ds-table-cell whitespace-nowrap`}>
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                            <span className="text-sm font-medium text-text-enterprise-primary">
                              {candidate.parsed_fields?.experience_years ? `${candidate.parsed_fields.experience_years}y` : 'N/A'}
                            </span>
                          </div>
                      </td>
                        <td className={`px-3 ${cellY} ds-table-cell whitespace-nowrap`}>
                          <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                            {submissionCount > 0 ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewSubmissions(candidate.id);
                                }}
                                className="inline-flex items-center px-2 py-1 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 text-primary-300 text-xs rounded-full border border-primary-500/30 font-medium hover:from-primary-500/30 hover:to-secondary-500/30 hover:border-primary-500/50 transition-all duration-200 cursor-pointer whitespace-nowrap"
                              >
                                {submissionCount} submitted
                              </button>
                            ) : (
                              <span className="text-text-enterprise-tertiary text-xs font-medium">None</span>
                            )}
                          </div>
                        </td>
                        <td className={`px-3 ${cellY} ds-table-cell`}>
                          <div className="text-xs text-text-enterprise-secondary leading-tight min-w-0">
                            {(() => {
                              const getName = () => {
                                if (candidate.submissions && candidate.submissions.length > 0) {
                                  const s = candidate.submissions[0];
                                  return s.submitted_by_name || s.submitted_by || '—';
                                }
                                return candidate.created_by_name || candidate.uploaded_by_name || candidate.created_by || candidate.uploaded_by || '—';
                              };
                              const getDate = () => {
                                if (candidate.submissions && candidate.submissions.length > 0) {
                                  const s = candidate.submissions[0];
                                  return s.submitted_at ? formatDateTime(s.submitted_at) : '—';
                                }
                                return candidate.created_at ? formatDateTime(candidate.created_at as any) : '—';
                              };
                              const submittedByName = getName();
                              const submittedAtDate = getDate();
                              return (
                                <div className="min-w-0">
                                  <div className="font-medium text-text-enterprise-primary truncate text-sm" title={submittedByName}>
                                    {submittedByName}
                                  </div>
                                  <div className="text-xs text-text-enterprise-tertiary truncate" title={submittedAtDate}>
                                    {submittedAtDate}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                        <td className={`px-3 ${cellY} text-xs text-text-enterprise-secondary ds-table-cell whitespace-nowrap`}>
                          {formatDateTime(candidate.updated_at)}
                        </td>
                        <td className={`px-3 ${cellY} whitespace-nowrap ds-table-cell`}>
                          <div className="flex justify-end relative">
                            <button
                              className="p-2 rounded-lg bg-glass-surface/30 hover:bg-glass-surface/50 border border-glass-border/40 transition-colors"
                              onClick={(e) => openActionsMenu(e, candidate.id)}
                              aria-label="Open actions menu"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gradient-to-r from-glass-surface/20 to-glass-surface/10">
                        <td colSpan={6} className={`px-6 ${cellY}`}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Skills Section */}
                            <div>
                              <h4 className="text-sm font-semibold text-text-enterprise-primary mb-3 flex items-center gap-2">
                                <Target className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                Skills
                              </h4>
                              {(candidate.parsed_fields?.skills || []).length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {(candidate.parsed_fields?.skills || []).slice(0, 8).map((skill: string, index: number) => (
                                    <span key={index} className="px-2.5 py-1 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 text-primary-300 text-xs rounded border border-primary-500/30 font-medium">
                                      {skill}
                                    </span>
                                  ))}
                                  {(candidate.parsed_fields?.skills || []).length > 8 && (
                                    <span className="px-2.5 py-1 bg-glass-surface/50 text-text-enterprise-tertiary text-xs rounded border border-glass-border/30 font-medium">
                                      +{(candidate.parsed_fields?.skills || []).length - 8} more
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="text-sm text-text-enterprise-tertiary">No skills parsed</div>
                              )}
                            </div>

                            {/* Additional Details Section */}
                            <div>
                              <h4 className="text-sm font-semibold text-text-enterprise-primary mb-3 flex items-center gap-2">
                                <Briefcase className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                Additional Details
                              </h4>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-text-enterprise-secondary w-20">Position:</span>
                                  <span className="text-sm text-text-enterprise-primary">
                                    {candidate.parsed_fields?.current_position || 'Not specified'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-text-enterprise-secondary w-20">Location:</span>
                                  <span className="text-sm text-text-enterprise-primary">
                                    {candidate.parsed_fields?.location || 'Not specified'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-text-enterprise-secondary w-20">Education:</span>
                                  <span className="text-sm text-text-enterprise-primary">
                                    {candidate.parsed_fields?.education || 'Not specified'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-text-enterprise-secondary w-20">CV Status:</span>
                                  <span className="text-sm text-text-enterprise-primary">
                                    {candidate.cv_path ? (
                                      <span className="inline-flex items-center px-2 py-1 bg-green-500/20 text-green-700 dark:text-green-400 text-xs rounded border border-green-500/30 font-medium">
                                        Available
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-1 bg-background-300/20 dark:bg-background-dark-300/20 text-text-tertiary dark:text-text-tertiary text-xs rounded border border-border-200 dark:border-border-dark-200 font-medium">
                                        Not uploaded
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );})}
                </tbody>
              </table>
              </div>
              </div>
            </div>

              {/* Enhanced Mobile Card View */}
              <div className="lg:hidden">
                <div className="p-6 space-y-6">
                  {displayItems.map((candidate: Candidate) => (
                    <div key={candidate.id} className="bg-gradient-to-br from-glass-surface/95 to-glass-surface/90 backdrop-blur-xl rounded-2xl shadow-lg border border-glass-border/50 p-6 hover:shadow-xl transition-all duration-300">
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-14 h-14 bg-gradient-to-br from-primary-500/20 via-primary-600/30 to-secondary-500/20 rounded-full flex items-center justify-center border border-primary-500/30 flex-shrink-0">
                            <span className="text-primary-600 dark:text-primary-400 font-bold text-lg">
                              {candidate.name?.charAt(0)?.toUpperCase() || 'C'}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-text-enterprise-primary text-lg truncate">
                              {candidate.name || 'Unknown'}
                            </div>
                            <div className="text-sm text-text-enterprise-secondary truncate">
                              {candidate.email || 'No email'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewCandidate(candidate.id)}
                            className="p-2.5 text-text-enterprise-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-all duration-200 border border-transparent hover:border-primary-500/20"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewAnalysis(candidate.id)}
                            className="p-2.5 text-text-enterprise-tertiary hover:text-green-600 dark:hover:text-green-500 hover:bg-green-500/10 rounded-lg transition-all duration-200 border border-transparent hover:border-green-500/20"
                            title="View analysis"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCandidate(candidate);
                            }}
                            className="p-2.5 text-text-enterprise-tertiary hover:text-blue-600 dark:hover:text-blue-500 hover:bg-blue-500/10 rounded-lg transition-all duration-200 border border-transparent hover:border-blue-500/20"
                            title="Edit candidate"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                            <span className="text-sm font-medium text-text-enterprise-primary">Phone</span>
                          </div>
                          <span className="text-sm font-semibold text-text-enterprise-primary">
                            {candidate.phone || 'Not provided'}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
                          <div className="flex items-center gap-3">
                            <Briefcase className="w-4 h-4 text-green-600 dark:text-green-500" />
                            <span className="text-sm font-medium text-text-enterprise-primary">Experience</span>
                          </div>
                          <span className="text-sm font-semibold text-text-enterprise-primary">
                            {candidate.parsed_fields?.experience_years ? `${candidate.parsed_fields.experience_years} years` : 'Not specified'}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-lg border border-orange-500/20">
                          <div className="flex items-center gap-3">
                            <Award className="w-4 h-4 text-orange-600 dark:text-orange-500" />
                            <span className="text-sm font-medium text-text-enterprise-primary">Requirements</span>
                          </div>
                          {candidate.submissions && candidate.submissions.length > 0 ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewSubmissions(candidate.id);
                              }}
                              className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 text-primary-300 text-xs rounded-full border border-primary-500/30 font-medium hover:from-primary-500/30 hover:to-secondary-500/30 hover:border-primary-500/50 transition-all duration-200 cursor-pointer"
                            >
                              {candidate.submissions.length} submitted
                            </button>
                          ) : (
                            <span className="text-text-enterprise-tertiary text-xs font-medium">Not submitted</span>
                          )}
                        </div>
                        
                        {(candidate.parsed_fields?.skills || []).length > 0 && (
                          <div className="p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20">
                            <div className="flex items-center gap-3 mb-2">
                              <Target className="w-4 h-4 text-purple-500" />
                              <span className="text-sm font-medium text-text-enterprise-primary">Skills</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {(candidate.parsed_fields?.skills || []).slice(0, 6).map((skill: string, index: number) => (
                                <span
                                  key={index}
                                  className="px-2 py-0.5 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 text-primary-300 text-xs rounded border border-primary-500/30 font-medium"
                                  title={skill}
                                >
                                  {skill.length > 10 ? skill.substring(0, 10) + '...' : skill}
                                </span>
                              ))}
                              {(candidate.parsed_fields?.skills || []).length > 6 && (
                                <span className="px-2 py-0.5 bg-glass-surface/50 text-text-enterprise-tertiary text-xs rounded border border-glass-border/30 font-medium">
                                  +{(candidate.parsed_fields?.skills || []).length - 6}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="mt-4 bg-white dark:bg-surface-dark-100 rounded-xl p-4 border border-border-100 dark:border-border-dark-100 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="text-sm text-text-primary dark:text-text-primary">
                  Showing <span className="font-semibold">{((page - 1) * pageSize) + 1}</span> to <span className="font-semibold">{Math.min(page * pageSize, total)}</span> of <span className="font-semibold">{total}</span> candidates
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-text-secondary dark:text-text-secondary">Per page:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1); // Reset to first page when changing page size
                    }}
                    className="h-8 text-sm border border-border-100 dark:border-border-dark-100 rounded-md bg-white dark:bg-surface-dark-100 text-text-primary dark:text-text-primary px-2"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={() => setPage(prev => Math.max(prev - 1, 1))} disabled={page === 1} className="px-3 py-2">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-4 py-2 text-sm text-text-primary dark:text-text-primary bg-background-100 dark:bg-surface-dark-100 rounded-lg">
                    Page {page} of {totalPages}
                  </span>
                  <Button variant="outline" onClick={() => setPage(prev => Math.min(prev + 1, totalPages))} disabled={page === totalPages} className="px-3 py-2">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title="Search & Filters"
          onClear={() => {
            setSearchQuery('');
            setQuickFilter('all');
            setSelectedRequirement('all');
            setPage(1);
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-secondary mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-text-tertiary dark:text-text-tertiary w-4 h-4" />
                <Input
                  placeholder="Search candidates (name, email, phone)"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1); // Reset to first page on search
                  }}
                  className="pl-8 pr-2 h-9 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-secondary mb-1">Filter by Requirement/Portal</label>
              <select
                value={selectedRequirement}
                onChange={(e) => {
                  setSelectedRequirement(e.target.value);
                  setPage(1); // Reset to first page on filter change
                }}
                className="w-full h-9 text-sm border border-border-100 dark:border-border-dark-100 rounded-md bg-white dark:bg-surface-dark-100 text-text-primary dark:text-text-primary px-2"
              >
                <option value="all">All Requirements</option>
                {requirementsData?.requirements?.map((req: any) => (
                  <option key={req.id} value={req.id}>
                    {req.title || 'Untitled Requirement'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-secondary mb-1">Quick filter</label>
              <select
                value={quickFilter}
                onChange={(e) => {
                  setQuickFilter(e.target.value as any);
                  setPage(1); // Reset to first page on filter change
                }}
                className="w-full h-9 text-sm border border-border-100 dark:border-border-dark-100 rounded-md bg-white dark:bg-surface-dark-100 text-text-primary dark:text-text-primary px-2"
              >
                <option value="all">All Candidates</option>
                <option value="submitted">Submitted to Requirements</option>
                <option value="unsubmitted">Not Submitted</option>
                <option value="processing">Processing CV</option>
                <option value="hasCV">Has CV</option>
              </select>
            </div>

            {/* Filter Summary */}
            {(searchQuery || selectedRequirement !== 'all' || quickFilter !== 'all') && (
              <div className="mt-4 p-3 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-lg border border-primary-500/20">
                <div className="text-xs font-medium text-text-primary dark:text-text-primary mb-2">Active Filters:</div>
                <div className="flex flex-wrap gap-2">
                  {searchQuery && (
                    <span className="inline-flex items-center px-2 py-1 bg-white dark:bg-surface-dark-100 text-xs rounded border border-border-100 dark:border-border-dark-100">
                      Search: "{searchQuery}"
                    </span>
                  )}
                  {selectedRequirement !== 'all' && (
                    <span className="inline-flex items-center px-2 py-1 bg-white dark:bg-surface-dark-100 text-xs rounded border border-border-100 dark:border-border-dark-100">
                      Requirement: {requirementsData?.requirements?.find((r: any) => r.id === selectedRequirement)?.title || 'Selected'}
                    </span>
                  )}
                  {quickFilter !== 'all' && (
                    <span className="inline-flex items-center px-2 py-1 bg-white dark:bg-surface-dark-100 text-xs rounded border border-border-100 dark:border-border-dark-100">
                      Filter: {quickFilter}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </FilterDrawer>

        {/* Candidate Form Modal */}
        {showCandidateForm && (
          <CandidateForm
            candidate={editingCandidate}
            onSubmit={editingCandidate ? handleUpdateCandidate : handleCreateCandidate}
            onClose={() => {
              setShowCandidateForm(false);
              setEditingCandidate(null);
            }}
            isLoading={createCandidateMutation.isPending || updateCandidateMutation.isPending}
          />
        )}

        {/* CV Upload Modal */}
        {showCVUpload && (
          <CVUploadModal
            onSuccess={handleCVUploadSuccess}
            onClose={() => setShowCVUpload(false)}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {deletingCandidate && (
          <ConfirmationDialog
            isOpen={!!deletingCandidate}
            onClose={() => setDeletingCandidate(null)}
            options={{
              title: "Delete Candidate",
              description: `Are you sure you want to delete ${deletingCandidate.name}? This action cannot be undone.`,
              confirmText: "Delete",
              cancelText: "Cancel",
              variant: "destructive",
              onConfirm: handleDeleteCandidate
            }}
          />
        )}
      </div>
    </Fragment>
  );
}
